import React, { useRef, useState, useEffect, useMemo, useCallback } from 'react';

import * as d3 from 'd3';
import { sankey } from 'd3-sankey';
import { getLabel } from "../functions/lang";

// CONSTANTS (match your backend/layout)
const H_NODE_THICKNESS = 12;
const NODE_WIDTH = 12;
const NODE_PADDING = 45;
const CORNER_RADIUS = 3;
const MAX_NAME_LENGTH = 35;

// Word-safe truncation (never cuts mid-word)
function truncateAtWordBoundary(text, maxLength = MAX_NAME_LENGTH) {
  if (!text || text.length <= maxLength) return text;
  const lastSpace = text.lastIndexOf(' ', maxLength);
  return lastSpace > 20 ? text.slice(0, lastSpace) + '…' : text.slice(0, maxLength) + '…';
}

// Recalculate link distribution maps (exact port)
function recalculateLinkDistributions(graphNodes, graphLinks) {
  // Vertical target centers (Top → Bottom)
  const targetYMap = new Map();
  graphLinks.forEach(link => {
    if (!targetYMap.has(link.target)) targetYMap.set(link.target, link.target.y0);
    link._targetYCenter = targetYMap.get(link.target) + link.width / 2;
    targetYMap.set(link.target, targetYMap.get(link.target) + link.width);
  });

  const targetYVMap = new Map();
  const verticalTargetLinks = graphLinks
    .filter(l => l.target.nodeOrientation === 'vertical')
    .sort((a, b) => a.y0 - b.y0);
  verticalTargetLinks.forEach(link => {
    if (!targetYVMap.has(link.target)) targetYVMap.set(link.target, link.target.y0);
    link._targetYCenter = targetYVMap.get(link.target) + link.width / 2;
    targetYVMap.set(link.target, targetYVMap.get(link.target) + link.width);
  });

  // Horizontal source edges
  const sourceLinks = graphLinks.filter(l => l.source.nodeOrientation === 'horizontal');
  const upLinks = sourceLinks.filter(l => l.target.y0 < l.source.y0).sort((a, b) => a.y1 - b.y1);
  const downLinks = sourceLinks.filter(l => l.target.y0 >= l.source.y0).sort((a, b) => a.y1 - b.y1);

  const sourceXMapUp = new Map();
  const sourceXMapDown = new Map();

  upLinks.forEach((link, i) => {
    link._indexInStack = i;
    if (!sourceXMapUp.has(link.source)) {
      sourceXMapUp.set(link.source, link.source.x1 + (link.source.y1 - link.source.y0));
    }
    link._sourceXEdge = sourceXMapUp.get(link.source) + link.width / 2;
    sourceXMapUp.set(link.source, sourceXMapUp.get(link.source) + link.width);
  });

  downLinks.forEach((link, i) => {
    link._indexInStack = i;
    if (!sourceXMapDown.has(link.source)) {
      sourceXMapDown.set(link.source, link.source.x0 + (link.source.y1 - link.source.y0));
    }
    link._sourceXEdge = sourceXMapDown.get(link.source) - link.width / 2;
    sourceXMapDown.set(link.source, sourceXMapDown.get(link.source) - link.width);
  });

  // Horizontal target edges from LEAF sources
  const targetLeafLinks = graphLinks.filter(
    l => l.target.nodeOrientation === 'horizontal' && l.source.nodeType !== 'middle'
  );
  const targetUpLinks = targetLeafLinks
    .filter(l => l.source.y0 > l.target.y0)
    .sort((a, b) => a.y0 - b.y0);
  const targetDownLinks = targetLeafLinks
    .filter(l => l.source.y0 <= l.target.y0)
    .sort((a, b) => a.y0 - b.y0);

  const targetXMapUp = new Map();
  const targetXMapDown = new Map();

  targetUpLinks.forEach((link, i) => {
    link._indexInStack = i;
    if (!targetXMapUp.has(link.target)) {
      targetXMapUp.set(link.target, link.target.x1);
    }
    link._targetXEdge = targetXMapUp.get(link.target) + link.width / 2;
    targetXMapUp.set(link.target, targetXMapUp.get(link.target) + link.width);
  });

  targetDownLinks.forEach((link, i) => {
    link._indexInStack = i;
    if (!targetXMapDown.has(link.target)) {
      targetXMapDown.set(link.target, link.target.x0);
    }
    link._targetXEdge = targetXMapDown.get(link.target) - link.width / 2;
    targetXMapDown.set(link.target, targetXMapDown.get(link.target) - link.width);
  });

  // Horizontal target approach from MIDDLE sources
  const targetMiddleLinks = graphLinks.filter(
    l => l.target.nodeOrientation === 'horizontal' && l.source.nodeType === 'middle'
  );
  const targetMidUpLinks = targetMiddleLinks
    .filter(l => l.source.y0 > l.target.y0)
    .sort((a, b) => a.y0 - b.y0);
  const targetMidDownLinks = targetMiddleLinks
    .filter(l => l.source.y0 <= l.target.y0)
    .sort((a, b) => a.y0 - b.y0);

  const targetApproachMapUp = new Map();
  const targetApproachMapDown = new Map();

  targetMidUpLinks.forEach((link, i) => {
    link._indexInStack = i;
    if (!targetApproachMapUp.has(link.target)) {
      targetApproachMapUp.set(link.target, link.target.x1);
    }
    link._targetApproachX = targetApproachMapUp.get(link.target) + link.width / 2;
    targetApproachMapUp.set(link.target, targetApproachMapUp.get(link.target) + link.width);
  });

  targetMidDownLinks.forEach((link, i) => {
    link._indexInStack = i;
    if (!targetApproachMapDown.has(link.target)) {
      targetApproachMapDown.set(link.target, link.target.x0);
    }
    link._targetApproachX = targetApproachMapDown.get(link.target) - link.width / 2;
    targetApproachMapDown.set(link.target, targetApproachMapDown.get(link.target) - link.width);
  });
}

// Path Generator with Arcs (exact port)
function getOrthogonalPath(link, index, allLinks) {
  const isSrcH = link.source.nodeOrientation === 'horizontal';
  const isTgtH = link.target.nodeOrientation === 'horizontal';

  const sx = isSrcH
    ? link._sourceXEdge ?? (link.source.x0 + (link.source.y1 - link.source.y0) / 2)
    : link.source.x1;
  const sy = isSrcH
    ? link.source.y0 + H_NODE_THICKNESS / 2
    : link.source.y0 + link._y0Offset;

  const tx = isTgtH
    ? link._targetXEdge ?? (link.target.x0 + (link.target.y1 - link.target.y0) / 2)
    : link.target.x0;
  const ty = isTgtH
    ? link.target.y0 - H_NODE_THICKNESS / 2
    : link._targetYCenter;

  if (isNaN(sx) || isNaN(sy) || isNaN(tx) || isNaN(ty)) return '';

  const orientation = link.orientation || 'horizontal';

  if (orientation === 'horizontal') {
    const mx1 = sx + (tx - sx) * 0.25;
    const mx2 = sx + (tx - sx) * 0.75;
    return `M${sx},${sy} C${mx1},${sy} ${mx2},${ty} ${tx},${ty}`;
  }

  if (orientation === 'vertical') {
      const finalTy = isTgtH
        ? link.target.y0 - H_NODE_THICKNESS / 2
        : link._targetYCenter;

      let finalTx;
      if (isTgtH) {
        finalTx =
          link.source.nodeType === 'middle'
            ? link._targetApproachX ?? link.target.x0
            : link._targetXEdge ?? link.target.x0;
      } else {
        finalTx = link.target.x0;
      }


      const r = CORNER_RADIUS;

      if (isTgtH) {
        const goingDown = finalTy > sy;
        const sweep = goingDown ? 1 : 0;
        const arcEndY = sy + (goingDown ? r : -r);
        return `M${sx},${sy} L${finalTx - r},${sy} A${r},${r} 0 0,${sweep} ${finalTx},${arcEndY} L${finalTx},${finalTy}`;
      }

      if (isSrcH) {
        const goingDown = sy < finalTy;
        const approachY = goingDown ? finalTy - r : finalTy + r;
        const sweep = goingDown ? 0 : 1;
        return `M${sx},${sy} L${sx},${approachY} A${r},${r} 0 0,${sweep} ${sx + r},${finalTy} L${finalTx},${finalTy}`;
      }

      //fallback for middle-to-middle vertical links (straight line)
      const midX = sx + (tx - sx) * 0.5;
      return `M${sx},${sy} C${midX},${sy} ${midX},${ty} ${tx},${ty}`;
  }

  // Fallback orthogonal
  const gap = tx - sx;
  const minRatio = 0.35;
  const maxRatio = 0.65;
  const total = allLinks?.length || 1;
  const ratio = minRatio + (index / Math.max(1, total - 1)) * (maxRatio - minRatio);
  const midX = sx + gap * ratio;
  const r = 4;
  const goingUp = sy > ty;
  const sweep1 = goingUp ? 0 : 1;
  const sweep2 = goingUp ? 1 : 0;

  return `M${sx},${sy} L${midX - r},${sy} A${r},${r} 0 0,${sweep1} ${midX},${
    sy + (goingUp ? -r : r)
  } L${midX},${ty + (goingUp ? r : -r)} A${r},${r} 0 0,${sweep2} ${midX + r},${ty} L${tx},${ty}`
    .trim()
    .replace(/\s+/g, ' ');
}


export default function SankeyD3Chart({
  json,
  language,
  sankeyName = 'Sankey Chart',
  year = new Date().getFullYear(),
  margin = { top: 20, right: 20, bottom: 20, left: 20 },
}) {
  const containerRef = useRef(null);
  const svgRef = useRef(null);
  const zoomRef = useRef(null);
  const tooltipRef = useRef(null);
  const [isMobile, setIsMobile] = useState(false);

  // Mobile detection
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  const height = isMobile ? 450 : 600;

  // Memoize processed data to avoid re-renders
  const processedData = useMemo(() => {
    if (json?.data?.length === 0) return null;

    let data = json.data;
    data = data.map(item => ({
        ...item,
        source: getLabel(item.source, language),
        target: getLabel(item.target, language),
        unit: getLabel(item.unit, language),
        product_name: getLabel(item.product_name, language),
    }));
    let positions = JSON.parse(json.sankey?.published_positions ?? '{}');

    let nodeNames = new Set();
    data.forEach(item => {
        nodeNames.add(item.source);
        nodeNames.add(item.target);
    });

    let sources = data.map(d => d.source);
    let targets = data.map(d => d.target);

    let nodes = Array.from(nodeNames).map(name => {
        const unit = data.find(d => d.source === name || d.target === name)?.unit || '';
        if (!sources.includes(name) && targets.includes(name)) {
            let target_en = data.find(d => d.target === name).target_en || name;
            return { name, node_id: target_en, itemStyle: { color: "#cccccc" }, unit };
        } else if (sources.includes(name) && !targets.includes(name)) {
            let source_en = data.find(d => d.source === name).source_en || name;
            return { name, node_id: source_en, itemStyle: { color: "#cccccc" }, unit }
        } else { 
            let firstLink = data.find(d => d.source === name);
            return { name, node_id: firstLink.source_en, itemStyle: { color: firstLink.color }, unit };
        }
    });
  
    let links = data.map(d => {
        return {
            ...d,
            value: Math.abs(d.value), //all negative value to positive
            lineStyle: {
                color: d.color,
                opacity: 0.8
            }
        }
    });
    return {nodes, links, positions};
  }, [json, language]);

  const exportAsPNG = useCallback((filename = 'sankey-chart.png') => {
    const svgEl = svgRef.current;
    if (!svgEl) {
      console.warn('SVG not ready for export');
      return;
    }

    const bbox = svgEl.getBBox();
    const TITLE_HEIGHT = 50;
    const exportWidth = bbox.width + 40;
    const exportHeight = bbox.height + TITLE_HEIGHT + 60;

    const clone = svgEl.cloneNode(true);
    clone.setAttribute('width', exportWidth);
    clone.setAttribute('height', exportHeight);
    clone.setAttribute(
      'viewBox',
      `${bbox.x - 20} ${bbox.y - TITLE_HEIGHT - 40} ${exportWidth} ${exportHeight}`
    );

    // Add title
    const titleGroup = document.createElementNS(
      'http://www.w3.org/2000/svg',
      'g'
    );
    titleGroup.setAttribute('transform', `translate(${exportWidth / 2}, ${-TITLE_HEIGHT / 2})`);

    const titleText = document.createElementNS(
      'http://www.w3.org/2000/svg',
      'text'
    );
    titleText.setAttribute('text-anchor', 'middle');
    titleText.setAttribute('font-family', 'system-ui, -apple-system, sans-serif');
    titleText.setAttribute('font-size', '16px');
    titleText.setAttribute('font-weight', '600');
    titleText.setAttribute('fill', '#1a1a1a');
    titleText.textContent = `${sankeyName} — ${year}`;

    titleGroup.appendChild(titleText);
    clone.insertBefore(titleGroup, clone.firstChild);

    // Inline styles
    const style = document.createElement('style');
    style.textContent = `
      .node { fill: #1f77b4; stroke: #111; stroke-width: 1.5; rx: 3; }
      .link { fill: none; stroke: #999; stroke-width: 1; stroke-opacity: 0.85; }
      text { font-family: system-ui, sans-serif; font-size: 12px; fill: #000; }
    `;
    clone.insertBefore(style, clone.firstChild);

    const svgData = new XMLSerializer().serializeToString(clone);
    const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(svgBlob);

    const img = new Image();
    img.crossOrigin = 'anonymous';

    img.onload = function () {
      const canvas = document.createElement('canvas');
      canvas.width = exportWidth * 2;
      canvas.height = exportHeight * 2;
      const ctx = canvas.getContext('2d');
      ctx.scale(2, 2);
      ctx.fillStyle = '#fafafa';
      ctx.fillRect(0, 0, exportWidth, exportHeight);
      ctx.drawImage(img, 0, 0);

      canvas.toBlob(function (blob) {
        const link = document.createElement('a');
        link.download = filename;
        link.href = URL.createObjectURL(blob);
        link.click();
        URL.revokeObjectURL(link.href);
        URL.revokeObjectURL(url);
      }, 'image/png');
    };

    img.onerror = function () {
      console.error('Failed to load SVG for PNG export');
      URL.revokeObjectURL(url);
      const link = document.createElement('a');
      link.download = filename.replace('.png', '.svg');
      link.href = url;
      link.click();
    };

    img.src = url;
  },
  [sankeyName, year] 
);

const exportAsHTML = useCallback(
  (filename = 'sankey-chart.html') => {
    // 🔑 Use ref instead of local svg variable
    const svgEl = svgRef.current;
    if (!svgEl) {
      console.warn('SVG not ready for HTML export');
      return;
    }

    const svgString = new XMLSerializer().serializeToString(svgEl);
    
    // 🔑 Get zoom transform from ref
    const zoomTransform = d3.zoomTransform(svgEl);
    const { x = 0, y = 0, k = 1 } = zoomTransform;

    const htmlContent = `<!DOCTYPE html>
    <html lang="en">
    <head>
      <meta charset="UTF-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <title>${sankeyName} — ${year}</title>
      <style>
        body { margin: 0; padding: 20px; background: #fafafa; font-family: system-ui, sans-serif; }
        .sankey-header { text-align: center; padding: 15px 0 25px; border-bottom: 1px solid #e0e0e0; margin-bottom: 20px; }
        .sankey-title { font-size: 20px; font-weight: 600; color: #1a1a1a; margin: 0 0 5px 0; }
        .sankey-container { width: 100%; max-width: 1200px; margin: 0 auto; }
        svg { width: 100%; height: auto; display: block; background: #fafafa; }
        .node { fill: #1f77b4; stroke: #111; stroke-width: 1.5; rx: 3; }
        .link { fill: none; stroke: #999; stroke-width: 1; stroke-opacity: 0.85; cursor: pointer; }
        .link:hover { stroke-opacity: 1; stroke-width: 2; }
        text { font-size: 12px; fill: #000; font-weight: 500; paint-order: stroke; stroke: #fff; stroke-width: 2px; }
        .tooltip { position: fixed; background: rgba(0,0,0,0.9); color: #fff; padding: 8px 12px; border-radius: 4px; font-size: 11px; pointer-events: none; opacity: 0; transition: opacity 0.15s; z-index: 9999; max-width: 300px; word-wrap: break-word; }
      </style>
    </head>
    <body>
      <div class="sankey-container">
        <header class="sankey-header">
          <h1 class="sankey-title">${sankeyName} — ${year}</h1>
        </header>
        ${svgString}
      </div>
      <div class="tooltip" id="export-tooltip"></div>
      <script src="https://d3js.org/d3.v7.min.js"><\/script>
      <script>
        // Restore zoom/pan state
        const svg = document.querySelector('svg');
        const g = svg?.querySelector('g');
        if (g) {
          g.setAttribute('transform', 'translate(${x}, ${y}) scale(${k})');
        }
        
        // Add basic interactivity (tooltips)
        const tooltip = document.getElementById('export-tooltip');
        document.querySelectorAll('.link').forEach(link => {
          link.addEventListener('mouseover', function(e) {
            const d = this.__data__;
            if (d) {
              tooltip.innerHTML = '<strong>' + (d.source?.name || 'Source') + ' → ' + (d.target?.name || 'Target') + '</strong><br/>Value: ' + (d.value?.toLocaleString?.() || d.value || 'N/A');
              tooltip.style.opacity = 1;
              tooltip.style.left = (e.pageX + 15) + 'px';
              tooltip.style.top = (e.pageY - 10) + 'px';
            }
          });
          link.addEventListener('mousemove', function(e) {
            tooltip.style.left = (e.pageX + 15) + 'px';
            tooltip.style.top = (e.pageY - 10) + 'px';
          });
          link.addEventListener('mouseout', function() {
            tooltip.style.opacity = 0;
          });
        });
      <\/script>
    </body>
    </html>`;

    const blob = new Blob([htmlContent], { type: 'text/html;charset=utf-8' });
    const link = document.createElement('a');
    link.download = filename;
    link.href = URL.createObjectURL(blob);
    link.click();
    URL.revokeObjectURL(link.href);
  },
  [sankeyName, year] 
);

  // Main render effect
  useEffect(() => {
    if (!processedData || !containerRef.current) return;

    const container = containerRef.current;
    const width = container.clientWidth || 1000;

    // Clean previous render
    d3.select(container).selectAll('*').remove();

    const svg = d3
      .select(container)
      .append('svg')
      .attr('width', width)
      .attr('height', height)
      .attr('viewBox', `0 0 ${width} ${height}`)
      .style('background', '#fafafa')
      .style('overflow', 'hidden')
      .style('touch-action', 'pan-x pan-y');

    svgRef.current = svg.node();

    // Clipping boundary
    const clipId = `sankey-clip-${Date.now()}`;
    svg
      .append('defs')
      .append('clipPath')
      .attr('id', clipId)
      .append('rect')
      .attr('width', width)
      .attr('height', height);

    const g = svg.append('g');

    // Zoom setup (read-only: no drag, but restore saved viewport)
    const zoom = d3
      .zoom()
      .scaleExtent([0.1, 4])
      .translateExtent([[-50, -50], [width + 50, height + 50]])
      .on('zoom', event => g.attr('transform', event.transform));

    svg.call(zoom);
    zoomRef.current = zoom;

    // Restore saved viewport if available
    if (processedData.positions?.viewport) {
      const { x = 0, y = 0, scale = 1 } = processedData.positions.viewport;
      const transform = d3.zoomIdentity.translate(x, y).scale(scale);
      svg.call(zoom.transform, transform);
      g.attr('transform', `translate(${x}, ${y}) scale(${scale})`);
    }

    // ─── D3 Sankey Layout (only if positions not pre-computed) ───
    let graphNodes, graphLinks;
    const hasSavedPositions = processedData.nodes[0]?.x0 !== undefined;

    if (!hasSavedPositions) {
      const sankeyGenerator = sankey()
        .nodeId(d => d.name)
        .nodeWidth(NODE_WIDTH)
        .nodePadding(NODE_PADDING)
        .extent([[40, 50], [width - 160, height - 50]]);

      const result = sankeyGenerator({
        nodes: processedData.nodes,
        links: processedData.links,
      });
      graphNodes = result.nodes;
      graphLinks = result.links;
    } else {
      graphNodes = processedData.nodes;
      graphLinks = processedData.links;
    }

    // ─── Node Type & Orientation Assignment ───
    graphNodes.forEach(node => {
      const isSource = graphLinks.some(l => l.source === node);
      const isTarget = graphLinks.some(l => l.target === node);
      node.nodeType = isSource && isTarget ? 'middle' : isSource ? 'source' : 'target';
    });

    const middleNodes = graphNodes.filter(n => n.nodeType === 'middle');
    const isDepth1 = middleNodes.length === 0;

    graphNodes.forEach(node => {
      if (node.nodeType === 'middle') {
        node.nodeOrientation = 'vertical';
      } else if (node.nodeType === 'source') {
        const emitsVertical = graphLinks.some(
          l => l.source === node && l.orientation === 'vertical'
        );
        node.nodeOrientation = emitsVertical ? 'horizontal' : 'vertical';
      } else if (node.nodeType === 'target') {
        if (isDepth1) {
          node.nodeOrientation = 'vertical';
        } else {
          const allTouched = graphLinks.filter(l => l.target === node);
          const receivesVertical =
            allTouched.length ===
            allTouched.filter(l => l.orientation === 'vertical').length;
          node.nodeOrientation = receivesVertical ? 'horizontal' : 'vertical';
        }
      } else {
        node.nodeOrientation = 'vertical';
      }
    });

    // ─── Store Relative Offsets ───
    graphLinks.forEach(link => {
      link._y0Rel = link.y0 - link.source.y0;
      link._y1Rel = link.y1 - link.target.y0;
      link._y0Offset = link._y0Rel;
      link._y1Offset = link._y1Rel;
    });

    // ─── Restore Saved Positions ───
    if (processedData.positions?.nodes && Array.isArray(processedData.positions.nodes)) {
      graphNodes.forEach(node => {
        const saved = processedData.positions.nodes.find(
          s => s.id === (node.node_id || node.name)
        );
        if (saved && typeof saved.x0 === 'number' && typeof saved.y0 === 'number') {
          node.x0 = saved.x0;
          node.y0 = saved.y0;
        }
      });
    }

    // ─── Recalculate Dimensions ───
    graphNodes.forEach(node => {
      const inWidth = graphLinks
        .filter(l => l.target === node)
        .reduce((sum, l) => sum + l.width, 0);
      const outWidth = graphLinks
        .filter(l => l.source === node)
        .reduce((sum, l) => sum + l.width, 0);
      const flowThickness = Math.max(inWidth, outWidth, 12);

      if (node.nodeOrientation === 'horizontal') {
        node.x1 = node.x0 - flowThickness;
        node.y1 = node.y0 + H_NODE_THICKNESS;
      } else {
        node.x1 = node.x0 + NODE_WIDTH;
        node.y1 = node.y0 + flowThickness;
      }
    });

    // ─── Update Link Attachments ───
    graphLinks.forEach(link => {
      link.y0 =
        link.source.nodeOrientation === 'horizontal'
          ? link.source.y0 + H_NODE_THICKNESS / 2
          : link.source.y0 + link._y0Rel;
      link.y1 =
        link.target.nodeOrientation === 'horizontal'
          ? link.target.y0 - H_NODE_THICKNESS / 2
          : link.target.y0 + link._y1Rel;
    });

    graphLinks.forEach(link => {
      link._y0Offset = link.y0 - link.source.y0;
      link._y1Offset = link.y1 - link.target.y0;
    });

    // ─── Recalculate Distributions (critical for arcs) ───
    recalculateLinkDistributions(graphNodes, graphLinks);

    // ─── Tooltip Setup ───
    const tooltip = d3
      .select('body')
      .append('div')
      .attr('class', 'sankey-tooltip')
      .style('position', 'absolute')
      .style('background', 'rgba(0,0,0,0.9)')
      .style('color', '#fff')
      .style('padding', '10px 14px')
      .style('border-radius', '6px')
      .style('pointer-events', 'none')
      .style('opacity', 0)
      .style('font-size', '12px')
      .style('font-family', 'system-ui, sans-serif')
      .style('z-index', 9999)
      .style('transition', 'opacity 0.15s');

    tooltipRef.current = tooltip.node();

    // ─── Render Links ───
    const linkGroup = g.append('g').attr('class', 'links');
    linkGroup
      .selectAll('path')
      .data(graphLinks)
      .join('path')
      .attr('d', (d, i, arr) => getOrthogonalPath(d, i, arr))
      .attr('fill', 'none')
      .attr('stroke', d => d.color || '#999')
      .attr('stroke-width', d => Math.max(1, d.width))
      .attr('stroke-opacity', 0.85)
      .style('cursor', 'default')
      .on('mouseover', function (event, d) {
        d3.select(this).attr('stroke-opacity', 1).attr('stroke-width', Math.max(2, d.width + 1));
        tooltip
          .style('opacity', 1)
          .html(
            `<strong>${d.source.name} → ${d.target.name}</strong><br/>
             Product: ${d.product_name || 'N/A'}<br/>
             Value: ${d.value?.toLocaleString?.() || d.value} ${d.unit || ''}`
          )
          .style('left', event.pageX + 15 + 'px')
          .style('top', event.pageY - 10 + 'px');
      })
      .on('mousemove', function (event) {
        tooltip.style('left', event.pageX + 15 + 'px').style('top', event.pageY - 10 + 'px');
      })
      .on('mouseout', function () {
        d3.select(this).attr('stroke-opacity', 0.85).attr('stroke-width', d => Math.max(1, d.width));
        tooltip.style('opacity', 0);
      });

    // ─── Render Nodes ───
    const nodeGroup = g.append('g').attr('class', 'nodes');
    const node = nodeGroup
      .selectAll('g')
      .data(graphNodes)
      .join('g')
      .attr('transform', d => `translate(${d.x0},${d.y0})`)
      .style('cursor', 'default');

    node
      .append('rect')
      .attr('width', d => {
        const w = d.nodeOrientation === 'horizontal' ? Math.abs(d.x1 - d.x0) : NODE_WIDTH;
        return Math.max(1, w);
      })
      .attr('height', d => {
        const h = d.nodeOrientation === 'horizontal' ? H_NODE_THICKNESS : Math.abs(d.y1 - d.y0);
        return Math.max(1, h);
      })
      .attr('x', d => {
        if (d.nodeOrientation !== 'horizontal') return 0;
        const width = Math.abs(d.x0 - d.x1);
        return d.nodeType === 'source' ? -width + H_NODE_THICKNESS : -width;
      })
      .attr('y', d =>
        d.nodeOrientation === 'horizontal' && d.nodeType === 'target' ? -H_NODE_THICKNESS : 0
      )
      .attr('fill', d => d.color || d.itemStyle?.color || '#1f77b4')
      .attr('rx', 3)
      .attr('stroke', '#111')
      .attr('stroke-width', 1.5);

    node
      .append('text')
      .attr('x', d => {
        if (d.nodeOrientation === 'horizontal') {
          return -Math.abs(d.x0 - d.x1) / 2;
        }
        return d.x0 < width / 2 ? NODE_WIDTH + 8 : -8;
      })
      .attr('y', d => {
        if (d.nodeOrientation === 'horizontal') {
          return d.y0 < height / 2 ? H_NODE_THICKNESS + 12 : -H_NODE_THICKNESS - 12;
        }
        return (d.y1 - d.y0) / 2;
      })
      .attr('dy', '0.35em')
      .attr('text-anchor', d =>
        d.nodeOrientation === 'horizontal' ? 'middle' : d.x0 < width / 2 ? 'start' : 'end'
      )
      .text(d => truncateAtWordBoundary(d.name || ''))
      .style('font-size', '12px')
      .style('font-family', 'system-ui, sans-serif')
      .style('fill', '#000')
      .style('font-weight', '500')
      .style('pointer-events', 'none')
      .style('paint-order', 'stroke')
      .style('stroke', '#ffffff')
      .style('stroke-width', '2px');

    // Attach full-name tooltip to nodes
    node.append('title').text(d => d.name || '');

    // Cleanup on unmount
    return () => {
      tooltip.remove();
      d3.select(container).selectAll('*').remove();
    };
  }, [processedData, height, margin, sankeyName, year]);

  // Expose export methods via ref if needed
  const api = useMemo(
    () => ({
      exportAsPNG: exportAsPNG,
      exportAsHTML: exportAsHTML,
    }),
    []
  );

  return (
     <div className="sankey-wrapper">
       <div className="screen-size-notice">
       <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
         <rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect>
         <line x1="8" y1="21" x2="16" y2="21"></line>
         <line x1="12" y1="17" x2="12" y2="21"></line>
       </svg>
       <span>Best viewed on desktop or tablet. Pinch to zoom & drag to explore on mobile.</span>
     </div>
     <div
       ref={containerRef}
       className="sankey-scroll-container"
       style={{ width: '100%', height, position: 'relative', marginTop: '5px', border: '1px solid #ccc', borderRadius: '5px' }}
     />
     </div>
  );
}